/**
 * Platform console: promotional coupons and campaigns
 * (docs/platform/coupons.md). Superadmin/support only - gated at the route
 * level by PlatformGuard, same as every other /platform/* page.
 *
 * Two coupon types only, both mapping onto mechanics the platform console
 * already has for a hotel's subscription (Platform.tsx's "manage
 * subscription" panel): FREE_SUBSCRIPTION pushes out currentPeriodEnd like
 * EXTEND_PLAN does, TRIAL_EXTENSION adds days to trialEndsAt like
 * EXTEND_TRIAL does. No percentage/fixed-amount discount types - there is no
 * SaaS checkout flow in this product yet for a discount to apply against.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, fieldErrors } from '../lib/api';
import { dateShort, dateTime } from '../lib/format';
import { PageHeader, Tabs } from '../components/Layout';
import { Empty, ErrorState, Loading, Modal, Select, TextInput, useToast } from '../components/ui';

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

type CouponType = 'FREE_SUBSCRIPTION' | 'TRIAL_EXTENSION';
type CouponStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'CANCELLED' | 'EXPIRED' | 'EXHAUSTED';

interface Coupon {
  id: string;
  code: string;
  name: string | null;
  campaignId: string | null;
  campaignName: string | null;
  type: CouponType;
  status: CouponStatus;
  freeMonths: number | null;
  trialExtensionDays: number | null;
  applicablePlanCodes: string[];
  usageLimit: number;
  usageCount: number;
  validFrom: string | null;
  validUntil: string | null;
  firstTimeCustomerOnly: boolean;
  benefitSummary: string;
  createdAt: string;
}

interface Campaign {
  id: string;
  name: string;
  description: string | null;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  createdAt: string;
  couponCount: number;
  redeemedCount: number;
}

interface PlanRow {
  code: string;
  name: string;
}

interface Redemption {
  id: string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  benefitSummary: string;
  redeemedAt: string;
  ip: string | null;
}

const TYPE_LABELS: Record<CouponType, string> = {
  FREE_SUBSCRIPTION: 'Free subscription',
  TRIAL_EXTENSION: 'Trial extension',
};

const STATUS_TONE: Record<CouponStatus, string> = {
  DRAFT: '',
  ACTIVE: 'ok',
  PAUSED: 'warn',
  CANCELLED: 'danger',
  EXPIRED: 'danger',
  EXHAUSTED: 'warn',
};

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

export function PlatformCouponsPage() {
  const [tab, setTab] = useState<'coupons' | 'campaigns'>('coupons');

  return (
    <div className="stack">
      <PageHeader
        title="Coupons"
        subtitle="Promotional codes redeemed at signup - free months, extended trials"
      />
      <Tabs
        tabs={[
          { id: 'coupons', label: 'Coupons' },
          { id: 'campaigns', label: 'Campaigns' },
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === 'coupons' ? <CouponsTab /> : <CampaignsTab />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Coupons tab                                                         */
/* ------------------------------------------------------------------ */

function CouponsTab() {
  const client = useQueryClient();
  const { push, fail } = useToast();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [viewing, setViewing] = useState<Coupon | null>(null);

  const queryString = new URLSearchParams({
    ...(status ? { status } : {}),
    ...(q ? { q } : {}),
    limit: '100',
  }).toString();

  const coupons = useQuery({
    queryKey: ['platform', 'coupons', status, q],
    queryFn: () => api.get<{ entries: Coupon[]; total: number }>(`/api/platform/coupons?${queryString}`),
  });
  const campaigns = useQuery({
    queryKey: ['platform', 'campaigns'],
    queryFn: () => api.get<Campaign[]>('/api/platform/campaigns'),
  });
  const plans = useQuery({
    queryKey: ['platform', 'plans'],
    queryFn: () => api.get<PlanRow[]>('/api/platform/plans'),
  });

  const refresh = () => void client.invalidateQueries({ queryKey: ['platform', 'coupons'] });

  const setStatusAction = useMutation({
    mutationFn: ({ id, status: next }: { id: string; status: 'ACTIVE' | 'PAUSED' | 'CANCELLED' }) =>
      api.post(`/api/platform/coupons/${id}/status`, { status: next }),
    onSuccess: () => {
      push('Coupon updated', 'ok');
      refresh();
    },
    onError: fail,
  });

  if (coupons.isLoading) return <Loading label="Reading coupons…" />;
  if (coupons.error) return <ErrorState error={coupons.error} onRetry={() => void coupons.refetch()} />;

  return (
    <div className="stack">
      <div className="row between wrap gap-6">
        <div className="row gap-6 wrap">
          <TextInput
            label="Search"
            placeholder="Search code…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Any status</option>
            <option value="ACTIVE">Active</option>
            <option value="DRAFT">Draft</option>
            <option value="PAUSED">Paused</option>
            <option value="CANCELLED">Cancelled</option>
          </Select>
        </div>
        <div className="row gap-6 wrap">
          <button className="btn" onClick={() => setGenerating(true)}>
            Generate coupons
          </button>
          <a
            className="btn"
            href={`/api/platform/coupons/export${queryString ? `?${queryString}` : ''}`}
            target="_blank"
            rel="noreferrer"
          >
            Export CSV
          </a>
          <button className="btn primary" onClick={() => setCreating(true)}>
            + New coupon
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {coupons.data!.entries.length === 0 ? (
            <Empty message="No coupons yet." />
          ) : (
            <div className="table-wrap"><table className="tbl tbl-stack">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Campaign</th>
                  <th>Offer</th>
                  <th className="num">Usage</th>
                  <th>Status</th>
                  <th>Valid until</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {coupons.data!.entries.map((coupon) => (
                  <tr key={coupon.id}>
                    <td className="b">
                      <button className="btn ghost sm" onClick={() => setViewing(coupon)}>
                        {coupon.code}
                      </button>
                    </td>
                    <td>{coupon.campaignName ?? '—'}</td>
                    <td>{coupon.benefitSummary}</td>
                    <td className="num">
                      {coupon.usageCount}/{coupon.usageLimit}
                    </td>
                    <td>
                      <span className={`chip ${STATUS_TONE[coupon.status]}`}>{coupon.status}</span>
                    </td>
                    <td className="nowrap dim">
                      {coupon.validUntil ? dateShort(coupon.validUntil) : 'no expiry'}
                    </td>
                    <td className="right">
                      <div className="row gap-6" style={{ justifyContent: 'flex-end' }}>
                        <button className="btn ghost sm" onClick={() => setEditing(coupon)}>
                          Edit
                        </button>
                        {coupon.status === 'ACTIVE' ? (
                          <button
                            className="btn ghost sm"
                            onClick={() => setStatusAction.mutate({ id: coupon.id, status: 'PAUSED' })}
                          >
                            Pause
                          </button>
                        ) : coupon.status === 'PAUSED' || coupon.status === 'DRAFT' ? (
                          <button
                            className="btn ghost sm"
                            onClick={() => setStatusAction.mutate({ id: coupon.id, status: 'ACTIVE' })}
                          >
                            Activate
                          </button>
                        ) : null}
                        {coupon.status !== 'CANCELLED' ? (
                          <button
                            className="btn ghost sm danger"
                            onClick={() => setStatusAction.mutate({ id: coupon.id, status: 'CANCELLED' })}
                          >
                            Cancel
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      </div>

      {creating || editing ? (
        <CouponModal
          coupon={editing}
          campaigns={campaigns.data ?? []}
          plans={plans.data ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {generating ? (
        <GenerateCouponsModal
          campaigns={campaigns.data ?? []}
          plans={plans.data ?? []}
          onClose={() => setGenerating(false)}
          onGenerated={refresh}
        />
      ) : null}

      {viewing ? <CouponDetailModal coupon={viewing} onClose={() => setViewing(null)} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Coupon create/edit modal                                             */
/* ------------------------------------------------------------------ */

interface CouponForm {
  code: string;
  autoCode: boolean;
  name: string;
  campaignId: string;
  type: CouponType;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'CANCELLED';
  freeMonths: number;
  trialExtensionDays: number;
  applicablePlanCodes: string[];
  singleUse: boolean;
  usageLimit: number;
  validFrom: string;
  validUntil: string;
  firstTimeCustomerOnly: boolean;
}

const BLANK_COUPON_FORM: CouponForm = {
  code: '',
  autoCode: true,
  name: '',
  campaignId: '',
  type: 'FREE_SUBSCRIPTION',
  status: 'ACTIVE',
  freeMonths: 3,
  trialExtensionDays: 30,
  applicablePlanCodes: [],
  singleUse: true,
  usageLimit: 1,
  validFrom: '',
  validUntil: '',
  firstTimeCustomerOnly: true,
};

function toForm(coupon: Coupon): CouponForm {
  return {
    code: coupon.code,
    autoCode: false,
    name: coupon.name ?? '',
    campaignId: coupon.campaignId ?? '',
    type: coupon.type,
    status: coupon.status === 'EXPIRED' || coupon.status === 'EXHAUSTED' ? 'ACTIVE' : coupon.status,
    freeMonths: coupon.freeMonths ?? 3,
    trialExtensionDays: coupon.trialExtensionDays ?? 30,
    applicablePlanCodes: coupon.applicablePlanCodes,
    singleUse: coupon.usageLimit === 1,
    usageLimit: coupon.usageLimit,
    validFrom: coupon.validFrom ? coupon.validFrom.slice(0, 16) : '',
    validUntil: coupon.validUntil ? coupon.validUntil.slice(0, 16) : '',
    firstTimeCustomerOnly: coupon.firstTimeCustomerOnly,
  };
}

/**
 * Shared by the single-coupon and bulk-generate forms - generic over the
 * caller's own form shape (GenerateCouponsModal's form has extra fields
 * like `prefix`/`quantity` that CouponForm does not) so each call site's
 * own `set` function, typed over its own wider or narrower form, is
 * accepted as-is rather than fighting a fixed `CouponForm` type here.
 */
function BenefitFields<
  F extends Pick<CouponForm, 'type' | 'freeMonths' | 'trialExtensionDays' | 'applicablePlanCodes'>,
>({
  form,
  set,
  plans,
  errors,
}: {
  form: F;
  set: <K extends keyof F>(key: K, value: F[K]) => void;
  plans: PlanRow[];
  errors: Record<string, string>;
}) {
  return (
    <>
      <div className="form-grid two">
        <Select label="Coupon type" value={form.type} onChange={(e) => set('type', e.target.value as CouponType)}>
          {(['FREE_SUBSCRIPTION', 'TRIAL_EXTENSION'] as const).map((type) => (
            <option key={type} value={type}>
              {TYPE_LABELS[type]}
            </option>
          ))}
        </Select>
        {form.type === 'FREE_SUBSCRIPTION' ? (
          <TextInput
            label="Free months"
            type="number"
            min={1}
            max={24}
            value={form.freeMonths}
            error={errors['freeMonths']}
            onChange={(e) => set('freeMonths', Number(e.target.value) || 1)}
          />
        ) : (
          <TextInput
            label="Extra trial days"
            type="number"
            min={1}
            max={365}
            value={form.trialExtensionDays}
            error={errors['trialExtensionDays']}
            onChange={(e) => set('trialExtensionDays', Number(e.target.value) || 1)}
          />
        )}
      </div>
      <div className="col gap-6">
        <label className="small b">Applicable plans</label>
        <span className="small muted">Leave all unchecked to allow any plan.</span>
        <div className="row gap-16 wrap">
          {plans.map((plan) => (
            <label key={plan.code} className="row gap-6">
              <input
                type="checkbox"
                checked={form.applicablePlanCodes.includes(plan.code)}
                onChange={(e) =>
                  set(
                    'applicablePlanCodes',
                    e.target.checked
                      ? [...form.applicablePlanCodes, plan.code]
                      : form.applicablePlanCodes.filter((c) => c !== plan.code),
                  )
                }
              />
              {plan.name}
            </label>
          ))}
        </div>
      </div>
    </>
  );
}

function CouponModal({
  coupon,
  campaigns,
  plans,
  onClose,
  onSaved,
}: {
  coupon: Coupon | null;
  campaigns: Campaign[];
  plans: PlanRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { push, fail } = useToast();
  const [form, setForm] = useState<CouponForm>(coupon ? toForm(coupon) : BLANK_COUPON_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof CouponForm>(key: K, value: CouponForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...(coupon ? {} : { code: form.autoCode ? undefined : form.code.trim() || undefined }),
        name: form.name.trim(),
        campaignId: form.campaignId || undefined,
        type: form.type,
        status: form.status,
        freeMonths: form.type === 'FREE_SUBSCRIPTION' ? form.freeMonths : undefined,
        trialExtensionDays: form.type === 'TRIAL_EXTENSION' ? form.trialExtensionDays : undefined,
        applicablePlanCodes: form.applicablePlanCodes,
        usageLimit: form.singleUse ? 1 : form.usageLimit,
        validFrom: form.validFrom ? new Date(form.validFrom).toISOString() : undefined,
        validUntil: form.validUntil ? new Date(form.validUntil).toISOString() : undefined,
        firstTimeCustomerOnly: form.firstTimeCustomerOnly,
      };
      return coupon ? api.patch(`/api/platform/coupons/${coupon.id}`, body) : api.post('/api/platform/coupons', body);
    },
    onSuccess: () => {
      push(coupon ? 'Coupon updated' : 'Coupon created', 'ok');
      onSaved();
    },
    onError: (error) => {
      setErrors(fieldErrors(error));
      fail(error);
    },
  });

  return (
    <Modal
      title={coupon ? `Edit ${coupon.code}` : 'New coupon'}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : coupon ? 'Save' : 'Create coupon'}
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <div className="form-grid two">
          <TextInput
            label="Internal name (optional)"
            placeholder="Diwali Restaurant Launch"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
          <Select
            label="Campaign (optional)"
            value={form.campaignId}
            onChange={(e) => set('campaignId', e.target.value)}
          >
            <option value="">No campaign</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>

        {!coupon ? (
          <div className="col gap-6">
            <label className="row gap-6">
              <input
                type="checkbox"
                checked={form.autoCode}
                onChange={(e) => set('autoCode', e.target.checked)}
              />
              Generate the code automatically
            </label>
            {!form.autoCode ? (
              <TextInput
                label="Custom code"
                placeholder="WELCOME3M"
                value={form.code}
                error={errors['code']}
                onChange={(e) => set('code', e.target.value.toUpperCase())}
              />
            ) : null}
          </div>
        ) : (
          <TextInput label="Code" value={coupon.code} disabled />
        )}

        <BenefitFields form={form} set={set} plans={plans} errors={errors} />

        <div className="form-grid three">
          <div className="col gap-6">
            <label className="small b">Usage limit</label>
            <label className="row gap-6">
              <input
                type="radio"
                checked={form.singleUse}
                onChange={() => set('singleUse', true)}
              />
              Single use
            </label>
            <label className="row gap-6">
              <input
                type="radio"
                checked={!form.singleUse}
                onChange={() => set('singleUse', false)}
              />
              Multiple uses
            </label>
            {!form.singleUse ? (
              <TextInput
                label="Custom limit"
                type="number"
                min={1}
                value={form.usageLimit}
                onChange={(e) => set('usageLimit', Number(e.target.value) || 1)}
              />
            ) : null}
          </div>
          <TextInput
            label="Valid from"
            type="datetime-local"
            value={form.validFrom}
            onChange={(e) => set('validFrom', e.target.value)}
          />
          <TextInput
            label="Valid until"
            type="datetime-local"
            value={form.validUntil}
            onChange={(e) => set('validUntil', e.target.value)}
          />
        </div>

        <label className="row gap-6">
          <input
            type="checkbox"
            checked={form.status === 'ACTIVE'}
            onChange={(e) => set('status', e.target.checked ? 'ACTIVE' : 'DRAFT')}
          />
          Active (usable immediately)
        </label>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Bulk generate                                                       */
/* ------------------------------------------------------------------ */

function GenerateCouponsModal({
  campaigns,
  plans,
  onClose,
  onGenerated,
}: {
  campaigns: Campaign[];
  plans: PlanRow[];
  onClose: () => void;
  onGenerated: () => void;
}) {
  const { push, fail } = useToast();
  const [form, setForm] = useState<CouponForm & { prefix: string; quantity: number }>({
    ...BLANK_COUPON_FORM,
    prefix: 'KORTEX',
    quantity: 100,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{ generated: number; codes: string[] } | null>(null);
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const generate = useMutation({
    mutationFn: () =>
      api.post<{ generated: number; requested: number; codes: string[] }>(
        '/api/platform/coupons/generate',
        {
          campaignId: form.campaignId || undefined,
          prefix: form.prefix.trim().toUpperCase(),
          quantity: form.quantity,
          name: form.name.trim() || undefined,
          type: form.type,
          freeMonths: form.type === 'FREE_SUBSCRIPTION' ? form.freeMonths : undefined,
          trialExtensionDays: form.type === 'TRIAL_EXTENSION' ? form.trialExtensionDays : undefined,
          applicablePlanCodes: form.applicablePlanCodes,
          usageLimit: form.singleUse ? 1 : form.usageLimit,
          validFrom: form.validFrom ? new Date(form.validFrom).toISOString() : undefined,
          validUntil: form.validUntil ? new Date(form.validUntil).toISOString() : undefined,
          firstTimeCustomerOnly: form.firstTimeCustomerOnly,
        },
      ),
    onSuccess: (data) => {
      push(`${data.generated} coupons generated`, 'ok');
      setResult(data);
      onGenerated();
    },
    onError: (error) => {
      setErrors(fieldErrors(error));
      fail(error);
    },
  });

  if (result) {
    const csv = ['Coupon Code', ...result.codes].join('\n');
    const dataUrl = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
    return (
      <Modal title="Coupons generated" onClose={onClose} footer={<button className="btn primary" onClick={onClose}>Done</button>}>
        <div className="col gap-16">
          <p className="small">
            {result.generated} coupon{result.generated === 1 ? '' : 's'} generated with prefix{' '}
            <b>{form.prefix.toUpperCase()}</b>.
          </p>
          <a className="btn" href={dataUrl} download={`coupons-${form.prefix.toLowerCase()}.csv`}>
            Download CSV
          </a>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Generate coupons"
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={generate.isPending} onClick={() => generate.mutate()}>
            {generate.isPending ? 'Generating…' : 'Generate'}
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <div className="form-grid two">
          <TextInput
            label="Code prefix"
            placeholder="DIWALI3M"
            value={form.prefix}
            error={errors['prefix']}
            onChange={(e) => set('prefix', e.target.value.toUpperCase())}
            hint="Every code looks like PREFIX-XXXXXX."
          />
          <TextInput
            label="How many"
            type="number"
            min={1}
            max={2000}
            value={form.quantity}
            error={errors['quantity']}
            onChange={(e) => set('quantity', Number(e.target.value) || 1)}
            hint="Up to 2,000 per batch (generated synchronously)."
          />
        </div>
        <Select label="Campaign (optional)" value={form.campaignId} onChange={(e) => set('campaignId', e.target.value)}>
          <option value="">No campaign</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <BenefitFields form={form} set={set} plans={plans} errors={errors} />
        <div className="col gap-6">
          <label className="small b">Usage limit per code</label>
          <label className="row gap-6">
            <input type="radio" checked={form.singleUse} onChange={() => set('singleUse', true)} />
            Single use
          </label>
          <label className="row gap-6">
            <input type="radio" checked={!form.singleUse} onChange={() => set('singleUse', false)} />
            Multiple uses
          </label>
          {!form.singleUse ? (
            <TextInput
              label="Custom limit"
              type="number"
              min={1}
              value={form.usageLimit}
              onChange={(e) => set('usageLimit', Number(e.target.value) || 1)}
            />
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Coupon detail (redemption history)                                  */
/* ------------------------------------------------------------------ */

function CouponDetailModal({ coupon, onClose }: { coupon: Coupon; onClose: () => void }) {
  const redemptions = useQuery({
    queryKey: ['platform', 'coupons', coupon.id, 'redemptions'],
    queryFn: () => api.get<Redemption[]>(`/api/platform/coupons/${coupon.id}/redemptions`),
  });

  return (
    <Modal title={coupon.code} size="wide" onClose={onClose} footer={<button className="btn" onClick={onClose}>Close</button>}>
      <div className="col gap-16">
        <div className="form-grid three small">
          <div>
            <div className="muted xs">Offer</div>
            <div className="b">{coupon.benefitSummary}</div>
          </div>
          <div>
            <div className="muted xs">Usage</div>
            <div className="b">
              {coupon.usageCount} / {coupon.usageLimit}
            </div>
          </div>
          <div>
            <div className="muted xs">Status</div>
            <span className={`chip ${STATUS_TONE[coupon.status]}`}>{coupon.status}</span>
          </div>
        </div>

        <div>
          <label className="small b">Redemption history</label>
          {redemptions.isLoading ? (
            <Loading label="Loading…" />
          ) : (redemptions.data ?? []).length === 0 ? (
            <Empty message="Not redeemed yet." />
          ) : (
            <div className="table-wrap"><table className="tbl tbl-stack">
              <thead>
                <tr>
                  <th>Hotel</th>
                  <th>Benefit</th>
                  <th>Redeemed</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.data!.map((r) => (
                  <tr key={r.id}>
                    <td>{r.tenantName}</td>
                    <td>{r.benefitSummary}</td>
                    <td className="dim">{dateTime(r.redeemedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Campaigns tab                                                       */
/* ------------------------------------------------------------------ */

function CampaignsTab() {
  const client = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);

  const campaigns = useQuery({
    queryKey: ['platform', 'campaigns'],
    queryFn: () => api.get<Campaign[]>('/api/platform/campaigns'),
  });

  const refresh = () => void client.invalidateQueries({ queryKey: ['platform', 'campaigns'] });

  if (campaigns.isLoading) return <Loading label="Reading campaigns…" />;
  if (campaigns.error) return <ErrorState error={campaigns.error} onRetry={() => void campaigns.refetch()} />;

  return (
    <div className="stack">
      <div className="row between">
        <span />
        <button className="btn primary" onClick={() => setCreating(true)}>
          + New campaign
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          {(campaigns.data ?? []).length === 0 ? (
            <Empty message="No campaigns yet." />
          ) : (
            <div className="table-wrap"><table className="tbl tbl-stack">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Dates</th>
                  <th>Status</th>
                  <th className="num">Coupons</th>
                  <th className="num">Redeemed</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {campaigns.data!.map((c) => (
                  <tr key={c.id}>
                    <td className="b">{c.name}</td>
                    <td className="small dim">
                      {c.startDate ? dateShort(c.startDate) : '…'} – {c.endDate ? dateShort(c.endDate) : '…'}
                    </td>
                    <td>
                      <span className={`chip ${c.isActive ? 'ok' : ''}`}>{c.isActive ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td className="num">{c.couponCount}</td>
                    <td className="num">{c.redeemedCount}</td>
                    <td className="right">
                      <button className="btn ghost sm" onClick={() => setEditing(c)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      </div>

      {creating || editing ? (
        <CampaignModal
          campaign={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function CampaignModal({
  campaign,
  onClose,
  onSaved,
}: {
  campaign: Campaign | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { push, fail } = useToast();
  const [form, setForm] = useState({
    name: campaign?.name ?? '',
    description: campaign?.description ?? '',
    startDate: campaign?.startDate ? campaign.startDate.slice(0, 10) : '',
    endDate: campaign?.endDate ? campaign.endDate.slice(0, 10) : '',
    isActive: campaign?.isActive ?? true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(),
        description: form.description.trim(),
        startDate: form.startDate ? new Date(form.startDate).toISOString() : undefined,
        endDate: form.endDate ? new Date(form.endDate).toISOString() : undefined,
        isActive: form.isActive,
      };
      return campaign
        ? api.patch(`/api/platform/campaigns/${campaign.id}`, body)
        : api.post('/api/platform/campaigns', body);
    },
    onSuccess: () => {
      push(campaign ? 'Campaign updated' : 'Campaign created', 'ok');
      onSaved();
    },
    onError: (error) => {
      setErrors(fieldErrors(error));
      fail(error);
    },
  });

  return (
    <Modal
      title={campaign ? `Edit ${campaign.name}` : 'New campaign'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!form.name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Saving…' : campaign ? 'Save' : 'Create campaign'}
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <TextInput
          label="Name"
          placeholder="Diwali Restaurant Launch 2026"
          value={form.name}
          error={errors['name']}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
        />
        <TextInput
          label="Description (optional)"
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
        />
        <div className="form-grid two">
          <TextInput
            label="Start date"
            type="date"
            value={form.startDate}
            onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
          />
          <TextInput
            label="End date"
            type="date"
            value={form.endDate}
            onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
          />
        </div>
        <label className="row gap-6">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
          />
          Active
        </label>
      </div>
    </Modal>
  );
}
